import React, { useEffect, useState } from 'react';
import styles from './TargetGroupTable.module.css';
import { ChevronDownIcon, ChevronUpIcon } from 'evergreen-ui';
import { formatDate } from '../../../utils/time';
import CandidateActivityRow from './CandidateActivityRow';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import { Skeleton } from '@material-ui/lab';
import { LightTooltip } from './TargetGroupTable';
import Collapse from '@material-ui/core/Collapse';
import Checkbox from '@material-ui/core/Checkbox';
import { Link } from 'react-router-dom';
import CandidateInfoModal from './CandidateInfoModal/CandidateInfoModal';
import { Assessment } from '../../../typings/assessment';
import { ReactComponent as ThreeDotsIcon } from '../../../assets/Icons/3dots.svg';
import { ReactComponent as ActivePDFIcon } from '../../../assets/Icons/active-pdf.svg';
import { ReactComponent as DisabledPDFIcon } from '../../../assets/Icons/disabled-pdf.svg';
import { Participant } from '../../../typings/participant';
import CandidateReportStatus from './CandidateReportStatus/CandidateReportStatus';

interface ParticipantRowProps {
    participant: Participant;
    participantsSelected: Participant[];
    usersShowingInfo: string[];
    isBatchProcessLoading: boolean;
    onUserShowInfoChange: (userId: string) => void;
    isUsersDetailsLoading: boolean;
    onSelect: (userId: string, event: any) => void;
    isSuperAdmin: boolean;
    invitationId: string;
    companyId: string;
    groupId: string;
    scan: Assessment;
    groupHasOptOutQuestionnaire: boolean;
    groupType: string;
}

const ParticipantRow = (props: ParticipantRowProps) => {
    const {
        participant,
        participantsSelected,
        usersShowingInfo,
        isBatchProcessLoading,
        onUserShowInfoChange,
        isUsersDetailsLoading,
        onSelect,
        isSuperAdmin,
        invitationId,
        scan,
        companyId,
        groupId,
        groupHasOptOutQuestionnaire,
        groupType,
    } = props;

    const hasReport = (participant: Participant): boolean => {
        return !!participant.report;
    };

    const isSelected = participantsSelected.some(
        (selected) => selected.user_id === participant.user_id,
    );
    const showInfo = usersShowingInfo.some((id) => id === participant.user_id);

    const [isCandidateDetailsModalOpen, setIsCandidateDetailsModalOpen] =
        useState<boolean>(false);

    const hasErrorStatus = Boolean(
        participant.errors && !!Object.keys(participant.errors).length,
    );

    useEffect(() => {
        if (isCandidateDetailsModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isCandidateDetailsModalOpen]);

    return (
        <React.Fragment key={`row-user-${participant.user_id}`}>
            {isBatchProcessLoading && (
                <>
                    <TableRow>
                        <TableCell colSpan={10} align="right">
                            <div className={styles.skeleton_container}>
                                <Skeleton
                                    variant="text"
                                    width="100%"
                                    height={40}
                                    animation="wave"
                                />
                            </div>
                        </TableCell>
                    </TableRow>
                </>
            )}
            {!isBatchProcessLoading && (
                <>
                    <TableRow hover selected={isSelected}>
                        <TableCell>
                            {showInfo && (
                                <ChevronUpIcon
                                    size={25}
                                    onClick={() => {
                                        onUserShowInfoChange(
                                            participant.user_id,
                                        );
                                    }}
                                    style={{ cursor: 'pointer' }}
                                />
                            )}
                            {!showInfo && (
                                <ChevronDownIcon
                                    size={25}
                                    onClick={() => {
                                        onUserShowInfoChange(
                                            participant.user_id,
                                        );
                                    }}
                                    style={{ cursor: 'pointer' }}
                                />
                            )}
                        </TableCell>
                        <TableCell>
                            <Checkbox
                                disabled={
                                    isUsersDetailsLoading ||
                                    isBatchProcessLoading
                                }
                                checked={isSelected}
                                onChange={(event) =>
                                    onSelect(participant.user_id, event)
                                }
                                color="primary"
                                classes={{ checked: styles.checkedBox }}
                            />
                        </TableCell>
                        <TableCell>
                            {participant.first_name
                                ? participant.first_name
                                : groupType === 'MOTIVIEW'
                                ? 'Motiview user'
                                : '-'}
                        </TableCell>
                        <TableCell>{participant.last_name ?? '-'}</TableCell>
                        <TableCell>{participant.email ?? '-'}</TableCell>
                        {isUsersDetailsLoading && (
                            <TableCell colSpan={5} align="right">
                                <div className={styles.skeleton_container}>
                                    <Skeleton
                                        variant="text"
                                        width="100%"
                                        height={20}
                                        animation="wave"
                                    />
                                </div>
                            </TableCell>
                        )}
                        {!isUsersDetailsLoading && (
                            <>
                                <TableCell align="center">
                                    <div>
                                        <p className={styles.register_info}>
                                            {formatDate(participant.invited_at)}
                                        </p>
                                    </div>
                                </TableCell>
                                <TableCell align="center">
                                    <CandidateReportStatus
                                        report={participant.report}
                                        analysis={participant.analysis}
                                        hasErrorStatus={hasErrorStatus}
                                        participantEmail={participant.email}
                                        groupType={groupType}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    {hasReport(participant) &&
                                    !!participant.report ? (
                                        <LightTooltip
                                            title={`Report generated on ${formatDate(
                                                participant.report.created_at,
                                            )}`}
                                            placement="left">
                                            <Link
                                                to={`results/${participant.user_id}`}
                                                target="_blank">
                                                <ActivePDFIcon />
                                            </Link>
                                        </LightTooltip>
                                    ) : !participant.email &&
                                      groupType === 'MOTIVIEW' &&
                                      participant.analysis?.status ===
                                          'done' ? (
                                        <LightTooltip
                                            title="No report for Motiview users"
                                            placement="left">
                                            <div>
                                                <DisabledPDFIcon />
                                            </div>
                                        </LightTooltip>
                                    ) : (
                                        <div>
                                            <DisabledPDFIcon />
                                        </div>
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    {!!participant.report
                                        ? formatDate(
                                              participant.report.created_at,
                                          )
                                        : '-'}
                                </TableCell>
                                <TableCell align="right">
                                    <div
                                        className={styles.info_modal}
                                        onClick={() => {
                                            setIsCandidateDetailsModalOpen(
                                                true,
                                            );
                                        }}>
                                        <ThreeDotsIcon />
                                    </div>
                                </TableCell>
                            </>
                        )}
                    </TableRow>

                    <TableRow>
                        <TableCell
                            style={{
                                padding: 0,
                                borderBottom: !showInfo ? 0 : undefined,
                                background: '#e7f8f8',
                            }}
                            colSpan={10}>
                            <Collapse in={showInfo} timeout="auto">
                                <CandidateActivityRow
                                    isSuperAdmin={isSuperAdmin}
                                    companyId={companyId}
                                    groupId={groupId}
                                    groupHasOptOutQuestionnaire={
                                        groupHasOptOutQuestionnaire
                                    }
                                    scanTemplateId={scan.id}
                                    participant={participant}
                                    invitationId={invitationId}
                                    scanTemplate={scan}
                                    showInfo={showInfo}
                                    groupType={groupType}
                                />
                            </Collapse>
                        </TableCell>
                    </TableRow>

                    {!isUsersDetailsLoading && isCandidateDetailsModalOpen && (
                        <TableRow>
                            <TableCell>
                                <CandidateInfoModal
                                    companyId={companyId}
                                    groupId={groupId}
                                    groupType={groupType}
                                    participant={participant}
                                    isSuperAdmin={isSuperAdmin}
                                    invitationId={invitationId}
                                    closeCandidateDetailsModal={() =>
                                        setIsCandidateDetailsModalOpen(false)
                                    }
                                />
                            </TableCell>
                        </TableRow>
                    )}
                </>
            )}
        </React.Fragment>
    );
};

export default ParticipantRow;
